import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Hotel, Users, Home, Coffee, Briefcase, ArrowRight } from 'lucide-react';

const Industries = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const industries = [
    {
      icon: Hotel,
      title: 'Hospitality',
      description:
        'Enhance guest experiences with premium workspace and entertainment solutions. Perfect for business hotels, extended-stay properties, and boutique accommodations.',
      benefits: ['Improved guest satisfaction', 'Competitive differentiation', 'Premium positioning', 'Repeat bookings'],
    },
    {
      icon: Briefcase,
      title: 'Business Travel Environments',
      description:
        'Create productive workspaces for business travelers who need professional setups for meetings, presentations, and extended work sessions.',
      benefits: ['Professional facilities', 'Meeting-ready setups', 'Reliable technology', 'Ergonomic comfort'],
    },
    {
      icon: Users,
      title: 'Co-living Spaces',
      description:
        'Modern shared living environments need quality workspaces and entertainment options. We design solutions for communal and private spaces.',
      benefits: ['Shared resource optimization', 'Flexible configurations', 'Scalable solutions', 'Community features'],
    },
    {
      icon: Home,
      title: 'Premium Guest Rooms',
      description:
        'Elevate guest rooms with elegant setups that support both work and leisure. Ideal for serviced apartments and high-end accommodations.',
      benefits: ['Premium aesthetics', 'Multi-purpose functionality', 'Easy maintenance', 'Guest satisfaction'],
    },
    {
      icon: Coffee,
      title: 'Lounges & Common Areas',
      description:
        'Transform lounges into productive hubs with comfortable workstations and entertainment zones that encourage longer stays.',
      benefits: ['Increased utilization', 'Revenue opportunities', 'Modern atmosphere', 'User engagement'],
    },
    {
      icon: Building2,
      title: 'Hybrid Workspaces',
      description:
        'Support flexible work arrangements with adaptable setups that serve multiple user types and work styles throughout the day.',
      benefits: ['Flexible usage', 'Space efficiency', 'Technology integration', 'User adaptability'],
    },
    {
      icon: Briefcase,
      title: 'Commercial Spaces',
      description:
        'Office buildings, corporate facilities, and commercial properties benefit from standardized, scalable workspace solutions.',
      benefits: ['Consistent quality', 'Easy scaling', 'Professional image', 'Operational efficiency'],
    },
  ];

  return (
    <div className="bg-[#FDFBF7] pt-20">
      {/* Hero Section */}
      <section className="py-24 sm:py-32" data-testid="industries-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-['Outfit'] text-5xl sm:text-6xl tracking-tight leading-none text-[#0A1F38] mb-6">
              Environments We Transform
            </h1>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] max-w-3xl mx-auto">
              From hospitality to hybrid workspaces, our solutions adapt to diverse environments and user
              needs. We're not limited to hotels—we create value wherever people work, stay, and play.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="pb-24 sm:pb-32" data-testid="industries-grid">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 sm:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-[#E2E8F0]/50 transition-all duration-300 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]"
                data-testid={`industry-${index}`}
              >
                <industry.icon className="text-[#C5A05B] mb-6" size={48} strokeWidth={1.5} />
                <h3 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight text-[#0A1F38] mb-4">
                  {industry.title}
                </h3>
                <p className="font-['Manrope'] text-base leading-relaxed text-[#475569] mb-6">
                  {industry.description}
                </p>
                <div className="space-y-2">
                  {industry.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#C5A05B]"></div>
                      <span className="font-['Manrope'] text-sm text-[#475569]">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-24 sm:py-32 bg-[#F5F3ED]" data-testid="industries-showcase">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-6">
                Designed for Real Users
              </h2>
              <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] mb-6">
                Our solutions come from real-world experience. As frequent users of hotels, guest
                environments, and commercial spaces, we understand what works and what doesn't.
              </p>
              <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569]">
                Every setup we create reflects this insight—combining premium aesthetics with the practical
                functionality that users actually need.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1774192621035-20d11389f781"
                alt="Premium business lounge"
                className="rounded-3xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32" data-testid="industries-cta">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-6">
              Ready to Discuss Your Environment?
            </h2>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] mb-10 max-w-2xl mx-auto">
              Let's explore how we can create premium workspace and gaming solutions for your specific
              industry and needs.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-10 py-5 rounded-full bg-[#0A1F38] text-[#FDFBF7] font-['Manrope'] font-semibold text-lg transition-all duration-300 hover:bg-[#0A1F38] hover:shadow-[0_20px_40px_rgba(0,0,0,0.15)] group"
              data-testid="industries-contact-cta"
            >
              Start a Conversation
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={24} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Industries;